import React from 'react';

import { Loader } from '@opendoor/bricks/core';
import { QuestionnaireWizard } from 'sellDirect/components/closingDash/disclosures/DynamicDisclosures/QuestionnaireWizard';
import { useUpdateDisclosure } from 'sellDirect/graphql/dynamicDisclosureHooks';

import { PromptGroup } from 'sellDirect/helpers/closingDash/disclosures';
import { UnderContractOffer } from 'sellDirect/OfferContext';
import { getHubSellingRoute } from 'shared/helpers/routes';
import { useHistory } from 'platform/HistoryProvider';
import { useObservability } from 'shared/helpers/observability';
import { GenericErrorPage } from 'shared/routes/errors/GenericErrorPage';

export interface DynamicDisclosureWizardProps {
  offer: UnderContractOffer;
}

/**
 * Renders new disclosures form based on questions from Rubric
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const DynamicDisclosuresWizard: React.FC<DynamicDisclosureWizardProps> = ({ offer }) => {
  const { goBack } = useHistory();
  const { trackError } = useObservability();

  const {
    id: offerId,
    leadToken,
    address: { uuid: addressUUID },
    disclosure,
  } = offer;
  const disclosureData = disclosure?.data;
  const disclosureLoading = disclosure?.loading;

  const update = useUpdateDisclosure(leadToken);

  if (!(disclosureData && disclosureData.rubric)) {
    if (disclosureLoading) {
      return <Loader />;
    }
    trackError('cake-dynamic-disclosures-missing-data', undefined, { offerId });
    return <GenericErrorPage from="dynamic-disclosures-page" />;
  }

  /** Verify if an empty array is a problem we should be tracking and displaying an error page instead. */
  const questionCategories: PromptGroup[] = disclosureData.rubric.promptGroups;

  const updateDisclosure = (isFinalized: boolean) => {
    if (disclosureData?.rubric && !disclosureData.finalizedAt) {
      update(isFinalized, disclosureData.id, {
        id: disclosureData.rubric.id,
        description: disclosureData.rubric.description,
        key: disclosureData.rubric.key,
        title: disclosureData.rubric.title,
        promptGroups: JSON.parse(
          JSON.stringify(questionCategories, (k, v) => (k === '__typename' ? undefined : v)),
        ),
      });
    }
  };

  return (
    <QuestionnaireWizard
      questionCategories={questionCategories}
      saveProgress={() => updateDisclosure(false)}
      submit={() => updateDisclosure(true)}
      onExit={() => goBack({ fallbackLocation: getHubSellingRoute(), addressUUID })}
    />
  );
};
